<template>
  <q-card flat bordered>
    <q-card-section
      class="q-pa-sm bg-primary row justify-between items-center text-white"
    >
      <div class="text-subtitle1 text-bold">
        <q-icon
          name="attach_money"
          style="font-size: 25px; padding-right: 0.1rem"
        />
        Pricing
      </div>
      <div class="row items-center q-gutter-x-sm">
        <slot name="header"></slot>
        <q-btn
          v-if="!readonly && action === 'view' && !inBatchUpdating"
          icon="edit"
          color="secondary"
          dense
          flat
          size="sm"
          @click="inBatchUpdating = true"
        />
        <q-btn
          v-if="!readonly && action === 'view' && inBatchUpdating"
          icon="check"
          color="primary"
          dense
          flat
          size="sm"
          @click="handleBatchUpdate"
        />
        <q-btn
          v-if="!readonly && action === 'view' && inBatchUpdating"
          icon="cancel"
          color="red"
          dense
          flat
          size="sm"
          @click="inBatchUpdating = false"
        />
        <q-btn
          v-if="!readonly"
          label="Run Auto Quoter"
          color="secondary"
          size="sm"
          :loading="runningTaq"
          @click="$emit('run-taq')"
        />
      </div>
    </q-card-section>
    <q-separator />
    <q-card-section>
      <div class="row">
        <div class="col">
          <div>
            <app-editable-input
              :loadingData="loadingData"
              :readonly="isReadOnlyField"
              :data="value.deposit"
              prefix="$"
              min="0"
              type="number"
              text="Deposit *"
              textClasses="text-weight-bold"
              name="deposit"
              @input="
                delayTouch(validation.deposit, $options.touchMap);
                handleChange($event);
              "
              :error="validation.deposit.$error"
              inputClasses="text-bold text-large"
            />
          </div>
          <div>
            <app-editable-input
              class="input-highlighted-1"
              :loadingData="loadingData"
              :readonly="isReadOnlyField"
              :data="carrierPay"
              prefix="$"
              type="number"
              min="0"
              text="Carrier Pay *"
              textClasses="text-weight-bold text-brand3"
              inputClasses="text-brand3 text-bold text-large"
              name="carrierPay"
              @input="handleCarrierPayChange($event.model)"
            />
          </div>
          <div style="min-height:40px">
            <app-editable-input
              v-if="fullPayEnabled"
              :loadingData="loadingData"
              :readonly="true"
              :data="fullPay"
              prefix="$"
              min="0"
              type="number"
              text="Full Pay"
              name="fullPay"
            />
          </div>
          <div>
            <app-editable-input
              class="input-highlighted-2"
              :loadingData="loadingData"
              :readonly="isReadOnlyField"
              :data="totalPrice"
              prefix="$"
              min="0"
              type="number"
              text="Total Price"
              textClasses="text-weight-bold text-secondary"
              inputClasses="text-secondary  text-bold text-large"
              name="totalPrice"
              @input="handleTotalPriceChange($event.model)"
              :error="validation.totalPrice.$error"
            />
          </div>
        </div>
        <div class="col">
          <div>
            <app-editable-input
              :loadingData="loadingData"
              :readonly="isReadOnlyField"
              :data="value.basePrice"
              prefix="$"
              type="number"
              min="0"
              text="Base Price *"
              name="basePrice"
              @input="
                delayTouch(validation.basePrice, $options.touchMap);
                handleChange($event);
              "
              :error="validation.basePrice.$error"
            />
          </div>
          <div>
            <app-editable-input
              :loadingData="loadingData"
              :readonly="isReadOnlyField"
              :data="value.inopFee"
              prefix="$"
              type="number"
              text="Inop Fee"
              name="inopFee"
              @input="handleChange($event)"
            />
          </div>
          <div>
            <app-editable-input
              :loadingData="loadingData"
              :readonly="isReadOnlyField"
              :data="value.sizeFee"
              prefix="$"
              type="number"
              text="Size Fee"
              name="sizeFee"
              @input="handleChange($event)"
            />
          </div>
          <div>
            <app-editable-input
              :loadingData="loadingData"
              :readonly="isReadOnlyField"
              :data="value.sizeFeeByDistance"
              prefix="$"
              type="number"
              text="Size Fee By Distance"
              name="sizeFeeByDistance"
              @input="handleChange($event)"
            />
          </div>
          <div>
            <app-editable-input
              :loadingData="loadingData"
              :readonly="isReadOnlyField"
              :data="value.depositFeeByDistance"
              prefix="$"
              type="number"
              text="Deposit Fee By Distance"
              name="depositFeeByDistance"
              @input="handleChange($event)"
            />
          </div>
          <div>
            <app-editable-input
              :loadingData="loadingData"
              :readonly="isReadOnlyField"
              :data="value.enclosedFee"
              prefix="$"
              type="number"
              text="Enclosed Fee"
              name="enclosedFee"
              @input="handleChange($event)"
            />
          </div>
        </div>
      </div>

      <slot name="append"></slot>
    </q-card-section>
  </q-card>
</template>
<script>
import AppEditableInput from '@client/components/form/editable-input';

export default {
  name: 'AppTransportPricingCard',
  components: {
    AppEditableInput
  },
  props: {
    loadingData: {
      type: Boolean,
      default: false
    },
    value: Object,
    fullPayEnabled: Boolean,
    runningTaq: {
      type: Boolean,
      default: false
    },
    context: {
      type: String,
      default: 'quote'
    },
    readonly: Boolean,
    action: {
      type: String,
      default: 'create'
    },
    validation: Object
  },
  data() {
    return {
      inBatchUpdating: false,
      showModifiers: false
    };
  },
  computed: {
    isReadOnlyField() {
      if (this.inBatchUpdating) return false;
      return this.readonly || this.action === 'view';
    },
    carrierPay() {
      return (
        this.value.basePrice +
        this.value.sizeFee +
        this.value.inopFee +
        this.value.sizeFeeByDistance +
        this.value.depositFeeByDistance +
        this.value.enclosedFee
      );
    },
    totalPrice() {
      return this.calcTotalPrice(this.value);
    },
    fullPay() {
      if (!this.fullPayEnabled) return 0;
      return Math.floor(
        (this.value.basePrice +
          this.value.sizeFee +
          this.value.inopFee +
          this.value.enclosedFee +
          this.value.deposit) *
          0.05
      );
    }
  },
  watch: {
    runningTaq: function (newVal, oldVal) {
      if (oldVal && !newVal) {
        this.handleTotalPriceChange(this.totalPrice);
      }
    },
    loadingData: function (newVal, oldVal) {
      if (oldVal && !newVal) {
        this.validation.totalPrice.$model = this.totalPrice;
      }
    }
  },
  methods: {
    calcTotalPrice(pricing) {
      const {
        basePrice = 0,
        sizeFee = 0,
        inopFee = 0,
        enclosedFee = 0,
        deposit = 0,
        sizeFeeByDistance = 0,
        depositFeeByDistance = 0
      } = pricing;

      const totalPrice = basePrice
        + sizeFee + inopFee + enclosedFee + deposit
        + sizeFeeByDistance + depositFeeByDistance;

      const fullPay = this.fullPayEnabled ? Math.floor(totalPrice * 0.05) : 0;

      return totalPrice + fullPay;
    },
    handleChange(value) {
      const { name, model } = value;

      const newValue = { ...this.value, [name]: parseInt(model, 10) };

      if (name === 'deposit') {
        newValue.basePrice = this.value.basePrice + this.value.deposit - model;
      } else {
        newValue.deposit = this.value.deposit + this.value[name] - model;
      }

      this.$emit('input', newValue);
    },
    handleCarrierPayChange(carrierPay) {
      const { sizeFee = 0, inopFee = 0, enclosedFee = 0 } = this.value;

      this.handleChange({
        name: 'basePrice',
        model: carrierPay - sizeFee - inopFee - enclosedFee
      });
    },
    handleTotalPriceChange(value) {
      const totalPrice = parseInt(value, 10);

      const {
        sizeFee = 0,
        inopFee = 0,
        enclosedFee = 0,
        deposit = 0
      } = this.value;

      const newValue = {
        ...this.value,
        basePrice:
          (this.fullPayEnabled ? Math.ceil(totalPrice / 1.03) : totalPrice) -
          sizeFee -
          inopFee -
          enclosedFee -
          deposit
      };

      this.$emit('input', newValue);

      this.validation.totalPrice.$model = totalPrice;
      this.delayTouch(this.validation.totalPrice, this.$options.touchMap);
    },
    handleBatchUpdate() {
      if (this.action === 'view') {
        this.$emit('update');
      }
    }
  }
};
</script>
<style lang="scss">
.input-highlighted-1 .q-field__prefix {
  color: var(--brand-3-color);
}
.input-highlighted-2 .q-field__prefix {
  color: var(--q-color-secondary);
}
</style>
